/**
 * Prototype-only: fill the upload form without leaving the browser.
 *
 * The point of this prototype is what happens *after* Submit, so picking a real
 * file off the disk every run is friction. This drives the form's own controls
 * from the outside — a click on the vendor select, a typed statement date, a
 * file handed to the file input — so the app code stays exactly as it ships.
 */
const MOCK_CSV = `ISRC,Track title,Release title,Artist,Units,Net revenue,Currency
GBKQU2100142,Night Ferry,Night Ferry,Halla Vey,18422,412.88,USD
GBKQU2100143,Harbour Lights,Night Ferry,Halla Vey,9310,208.44,USD
GBKQU2100177,Paper Anchor (Radio Edit),Paper Anchor,Halla Vey,44105,987.10,USD
,Coastal Roads,Coastal Roads,Marn Ottoline,2204,49.33,USD
GBKQU2100188,Slow Tide,Coastal Roads,Marn Ottoline,1180,1.2O3,USD
`;

export const MOCK_FILE_NAME = "spotify_q1_2026.csv";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Radix opens on pointerdown and selects on pointerup, so a bare click is not enough. */
function press(element: Element): void {
  for (const type of ["pointerdown", "mousedown", "pointerup", "mouseup", "click"]) {
    element.dispatchEvent(
      new PointerEvent(type, {
        bubbles: true,
        cancelable: true,
        button: 0,
        pointerId: 1,
        isPrimary: true,
        pointerType: "mouse",
      }),
    );
  }
}

/** React listens on the native event, so the value has to go in through the DOM setter. */
function setInputValue(input: HTMLInputElement, value: string): void {
  const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
  setter?.call(input, value);
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

function vendorTrigger(): HTMLElement | undefined {
  return [...document.querySelectorAll<HTMLElement>('[data-slot="select-trigger"]')].find(
    (trigger) => /revenue source|catalog source|Spotify/i.test(trigger.textContent ?? ""),
  );
}

async function vendorOption(vendorName: string): Promise<HTMLElement | undefined> {
  // The listbox is portalled in a render after the select opens.
  for (let attempt = 0; attempt < 40; attempt++) {
    const option = [
      ...document.querySelectorAll<HTMLElement>('[data-slot="select-item"], [role="option"]'),
    ].find((item) => item.textContent?.includes(vendorName));
    if (option) return option;
    await wait(25);
  }
  return undefined;
}

async function pickVendor(vendorName: string): Promise<boolean> {
  // The select stays disabled until the vendor list lands.
  let trigger = vendorTrigger();
  for (let attempt = 0; attempt < 40 && trigger?.hasAttribute("disabled"); attempt++) {
    await wait(50);
    trigger = vendorTrigger();
  }
  if (!trigger || trigger.hasAttribute("disabled")) return false;

  // ArrowDown on the focused trigger is the one open that Radix honours from a
  // synthetic event; a pointer press is the fallback.
  trigger.focus();
  trigger.dispatchEvent(
    new KeyboardEvent("keydown", { key: "ArrowDown", bubbles: true, cancelable: true }),
  );
  let option = await vendorOption(vendorName);
  if (!option) {
    press(trigger);
    option = await vendorOption(vendorName);
  }
  if (!option) return false;

  press(option);
  await wait(50);
  return vendorTrigger()?.textContent?.includes(vendorName) ?? false;
}

function fillStatementDate(): boolean {
  const input = document.querySelector<HTMLInputElement>("#statement_date");
  if (!input) return false;
  // Within the picker's accepted range: it refuses anything past the current year.
  setInputValue(input, "Mar 31, 2026");
  input.dispatchEvent(new Event("blur", { bubbles: true }));
  return true;
}

function attachFile(): boolean {
  const input = document.querySelector<HTMLInputElement>('input[type="file"]');
  if (!input) return false;
  const transfer = new DataTransfer();
  transfer.items.add(new File([MOCK_CSV], MOCK_FILE_NAME, { type: "text/csv" }));
  input.files = transfer.files;
  input.dispatchEvent(new Event("change", { bubbles: true }));
  return true;
}

export interface FillResult {
  attached: boolean;
  vendor: boolean;
  date: boolean;
}

/** Vendor, statement date and a mock CSV, in the order the form reads them. */
export async function fillUploadForm(): Promise<FillResult> {
  const vendor = await pickVendor("Spotify");
  const date = fillStatementDate();
  const attached = attachFile();
  return { attached, vendor, date };
}
